import { Box } from '@ironhack/design-system2/components/box';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import {
  ListItem,
  UnorderedList,
} from '@ironhack/design-system2/components/list';
import { SkeletonCircle } from '@ironhack/design-system2/components/skeleton';
import { Text } from '@ironhack/design-system2/components/text';
import { SpeedHeart } from '@ironhack/design-system2/icons/speed-heart';
import { type ReactElement } from 'react';

import { CmsImage } from '@/app/components/server/cms-image';
import { Icon } from '@/app/components/server/icon';
import {
  type DatoGradientColor,
  type DatoIconBlock,
  type DatoImage,
} from '@/lib/datocms/types';

type Profile = {
  heading: string;
  textLines: string[];
};

export type QuizMatchHeaderData = {
  circleBorderGradient: DatoGradientColor;
  matchIcon: DatoIconBlock;
  matchImage: DatoImage;
  titleBottom: string;
  titleColor: DatoGradientColor;
  titleTop: string;
};

export type QuizSummaryData = {
  summaryImage?: DatoImage;
  summaryProfile?: Profile;
};

type QuizMatchHeaderDataFull = {
  matchProfile?: Profile;
} & QuizMatchHeaderData &
  QuizSummaryData;

type Props = {
  showTitleIcon?: boolean;
  data: QuizMatchHeaderDataFull;
  size?: 'sm' | 'lg';
} & FlexProps;

export const QuizMatchHeader = (props: Props): ReactElement => {
  const {
    showTitleIcon = false,
    data: {
      circleBorderGradient: { gradientName: circleBorderColor },
      matchIcon,
      matchImage,
      matchProfile,
      summaryImage,
      summaryProfile,
      titleBottom,
      titleColor: { gradientName: titleColor },
      titleTop,
    },
    size = 'sm',
    ...flexProps
  } = props;

  const MATCH_SIDE_WIDTH = ['143px', null, size === 'sm' ? '155px' : '160px'];

  return (
    <Flex direction="column" textAlign="center" {...flexProps}>
      <Box
        fontStyle="italic"
        fontWeight="extrabold"
        maxWidth="600px"
        textTransform="uppercase"
        width="full"
      >
        {showTitleIcon && (
          <SpeedHeart color={titleColor} height="23px" width="38px" />
        )}
        <Text
          background={`background.${titleColor}`}
          bgClip="text"
          marginTop={0.5}
          textStyle="2xl"
        >
          {titleTop}
        </Text>
        <Text
          background={`background.${titleColor}`}
          bgClip="text"
          marginTop={-1}
          overflow="visible"
          textStyle="6xl"
        >
          {titleBottom}
        </Text>
      </Box>

      <Flex direction="column" gap={2} marginTop={1}>
        <Flex align="center" gap={size === 'sm' ? 1 : 2}>
          <Box
            background={`background.${circleBorderColor}`}
            borderRadius="full"
            boxSize={MATCH_SIDE_WIDTH}
            overflow="hidden"
            padding={0.5}
          >
            {summaryImage ? (
              <CmsImage
                data={summaryImage.responsiveImage}
                style={{ borderRadius: 'var(--chakra-radii-full)' }}
              />
            ) : (
              <SkeletonCircle boxSize="full" />
            )}
          </Box>

          <Icon boxSize="30px" icon={matchIcon} />

          <Box
            background={`background.${circleBorderColor}`}
            borderRadius="full"
            boxSize={MATCH_SIDE_WIDTH}
            overflow="hidden"
            padding={0.5}
          >
            <CmsImage
              data={matchImage.responsiveImage}
              style={{ borderRadius: 'var(--chakra-radii-full)' }}
            />
          </Box>
        </Flex>

        <Flex justify="space-between">
          {summaryProfile ? (
            <UnorderedList
              ml={0}
              spacing={0.5}
              styleType="none"
              textStyle="m"
              width={MATCH_SIDE_WIDTH}
            >
              <ListItem textStyle="lBold">{summaryProfile.heading}</ListItem>
              {summaryProfile.textLines.map((text: string) => (
                <ListItem key={text}>{text}</ListItem>
              ))}
            </UnorderedList>
          ) : (
            <Flex direction="column" gap={1.5} width={MATCH_SIDE_WIDTH}>
              <Box background="fill.medium" borderRadius="44px" height="12px" />
              <Box background="black.20" borderRadius="44px" height="12px" />
              <Box background="black.20" borderRadius="44px" height="12px" />
              <Box background="black.20" borderRadius="44px" height="12px" />
            </Flex>
          )}

          {matchProfile ? (
            <UnorderedList
              ml={0}
              spacing={0.5}
              styleType="none"
              textStyle="m"
              width={MATCH_SIDE_WIDTH}
            >
              <ListItem textStyle="lBold">{matchProfile.heading}</ListItem>
              {matchProfile.textLines.map((text: string) => (
                <ListItem key={text}>{text}</ListItem>
              ))}
            </UnorderedList>
          ) : (
            <Flex direction="column" gap={1.5} width={MATCH_SIDE_WIDTH}>
              <Box background="fill.medium" borderRadius="44px" height="12px" />
              <Box background="black.20" borderRadius="44px" height="12px" />
              <Box background="black.20" borderRadius="44px" height="12px" />
              <Box background="black.20" borderRadius="44px" height="12px" />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export const quizMatchHeaderFragment = `
  fragment quizMatchHeaderFragment on QuizMatchHeaderRecord {
    circleBorderGradient {
      gradientName
    }
    matchIcon {
      ...iconFragment
    }
    matchImage {
      responsiveImage(
        imgixParams: {
          auto: format
          cornerRadius: "100"
          fit: crop
          h: 152
          mask: corners
          q: 45
          w: 152
        }
      ) {
        ...responsiveImageFragment
      }
    }
    titleBottom
    titleColor {
      gradientName
    }
    titleTop
  }
`;
